import { CommonModule } from '@angular/common';
import {
  AfterViewChecked,
  Component,
  ElementRef,
  Input,
  ViewChild,
} from '@angular/core';
import { MatProgressSpinnerModule } from '@angular/material/progress-spinner';
import { ProcessLogEntry } from './process-log-entry';

/**
 * Reusable process-logs timeline + animated gradient loader.
 * Pass `logs` as an array (including []) to show the timeline; leave undefined/null to hide it.
 */
@Component({
  selector: 'app-process-logs-loader',
  standalone: true,
  imports: [CommonModule, MatProgressSpinnerModule],
  templateUrl: './process-logs-loader.component.html',
  styleUrl: './process-logs-loader.component.scss',
})
export class ProcessLogsLoaderComponent implements AfterViewChecked {
  @ViewChild('logsContainer') logsContainer?: ElementRef<HTMLDivElement>;

  /** When undefined/null, timeline is hidden. Empty array still shows the panel shell. */
  @Input() logs: ProcessLogEntry[] | null | undefined;

  /** Status label above the loader (e.g. Thinking..., Completed, Failed). */
  @Input() statusLabel = 'Thinking...';

  /** When true, shows the animated gradient loader. */
  @Input() showLoader = true;

  /** When true, treats process as still running (live thinking + spinner). */
  @Input() isActive = true;

  /** When true, greys out the gradient loader (error / stopped state). */
  @Input() isError = false;

  private lastLogCount = -1;
  private lastThinking = '';

  get showTimeline(): boolean {
    return Array.isArray(this.logs);
  }

  get completedEvaluations(): string[] {
    if (!this.logs?.length) {
      return [];
    }
    const keepLatestSeparate = this.isActive || this.isError;
    const end = keepLatestSeparate
      ? Math.max(0, this.logs.length - 1)
      : this.logs.length;
    return this.logs
      .slice(0, end)
      .map((log) => (log.evaluation || '').trim())
      .filter((evaluation) => !!evaluation);
  }

  get liveThinkingText(): string {
    if (!this.logs?.length) {
      return '';
    }
    // Completed successfully: fold latest into completed list
    if (!this.isActive && !this.isError) {
      return '';
    }
    const latest = this.logs[this.logs.length - 1];
    return this.getCleanThinking(latest?.thinking);
  }

  ngAfterViewChecked(): void {
    const thinking = this.liveThinkingText;
    const count = this.logs?.length ?? 0;
    if (count !== this.lastLogCount || thinking !== this.lastThinking) {
      this.lastLogCount = count;
      this.lastThinking = thinking;
      this.scrollToBottom();
    }
  }

  getCleanThinking(thinking?: string): string {
    if (!thinking) {
      return '';
    }
    const cutMarkers = ['<ctrl', 'call:AgentOutput', '{"current_state"'];
    let cleaned = thinking;
    for (const marker of cutMarkers) {
      const idx = cleaned.indexOf(marker);
      if (idx > 0) {
        cleaned = cleaned.slice(0, idx);
      }
    }
    return cleaned.trim();
  }

  private scrollToBottom(): void {
    const el = this.logsContainer?.nativeElement;
    if (el) {
      el.scrollTop = el.scrollHeight;
    }
  }
}
